<template>
  <div class="salary-slip-pdf" ref="salarySlipPDF">
    <div class="pdf-header">
      <div class="org-info-section">
        <div class="org-logo-container">
          <!-- <img
            v-if="orgLogoUrl"
            src="${orgLogoUrl}"
            alt="Organization Logo"
            class="org-logo"
            @error="handleImageError"
            crossorigin="anonymous"
          />
          <img
            v-else
            :src="defaultLogo"
            alt="Default Logo"
            class="org-logo"
            crossorigin="anonymous"
          /> -->
          <v-img
            :src="orgLogoUrl"
            v-if="orgLogoUrl"
            alt="Organization Logo"
            max-height="80"
            max-width="80"
            class="mx-auto"
          ></v-img>
          <v-img
            v-else
            :src="defaultLogo"
            alt="Default Logo"
            max-height="80"
            max-width="80"
            class="mx-auto"
          ></v-img>
        </div>
        <div class="org-details">
          <h2 class="org-name">{{ orgData ? orgData.name : 'Organization Name' }}</h2>
          <p class="org-address">{{ orgAddress(orgData?.organization_address) }}</p>
        </div>
      </div>
      <div class="pay-period">
        <h3>Pay Slip</h3>
        <p>For the month of {{ selectedMonthName }} {{ selectedYear }}</p>
        <p>(In INR)</p>
      </div>
    </div>

    <div class="divider"></div>

    <div class="employee-info-section">
      <div class="employee-details-grid">
        <div class="employee-details-column">
          <div class="detail-row">
            <span class="label">Employee Name</span>
            <span class="value">{{ employeeFullName(employeeData) }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Employee Code</span>
            <span class="value">{{ employeeData?.employeeId || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Bank Name</span>
            <span class="value">{{ bankDetails?.bankName || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Date of Joining</span>
            <span class="value">{{
              formatDate(employeeData?.employmentDetails?.joiningDate) || '-'
            }}</span>
          </div>
          <div class="detail-row">
            <span class="label">PF No</span>
            <span class="value">{{ employeeData?.employmentDetails?.pfNumber || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Location</span>
            <span class="value">{{ employeeData?.employmentDetails?.workLocation || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Standard Days</span>
            <span class="value">{{ standardDays || '-' }}</span>
          </div>
        </div>

        <div class="divider-vertical"></div>

        <div class="employee-details-column">
          <div class="detail-row">
            <span class="label">Designation</span>
            <span class="value">{{ employeeData?.designation?.designationName || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Bank A/C No</span>
            <span class="value">{{
              employeeData?.employmentDetails?.bankAccountNumber || '-'
            }}</span>
          </div>
          <div class="detail-row">
            <span class="label">PAN</span>
            <span class="value">{{ employeeData?.employmentDetails?.panNumber || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">PF UAN</span>
            <span class="value">{{ employeeData?.employmentDetails?.pfUAN || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">ESIC No</span>
            <span class="value">{{ employeeData?.employmentDetails?.esicNumber || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Work Days</span>
            <span class="value">{{ workDays || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">LOP Days</span>
            <span class="value">{{ lopDays || '-' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="divider"></div>

    <div class="salary-breakdown-section" v-if="salaryData">
      <div class="breakdown-container">
        <!-- Earnings Section -->
        <div class="earnings-section">
          <div class="section-header">
            <h3>EARNINGS</h3>
          </div>
          <div class="section-content">
            <div
              v-for="(earning, index) in formattedEarnings"
              :key="index"
              class="detail-row"
              :class="{ 'border-bottom': index < formattedEarnings.length - 1 }"
            >
              <span class="label">{{ earning.label || earning.name }}</span>
              <span class="value_salary">₹ {{ formatAmount(earning.amount) }}</span>
            </div>
            <div class="detail-row total-row">
              <span class="label">GROSS EARNINGS</span>
              <span class="value_salary">₹ {{ formatAmount(totalEarnings) }}</span>
            </div>
          </div>
        </div>

        <!-- Deductions Section -->
        <div class="deductions-section">
          <div class="section-header">
            <h3>DEDUCTIONS</h3>
          </div>
          <div class="section-content">
            <div
              v-for="(deduction, index) in formattedDeductions"
              :key="index"
              class="detail-row"
              :class="{ 'border-bottom': index < formattedDeductions.length - 1 }"
            >
              <span class="label">{{ deduction.label || deduction.name }}</span>
              <span class="value_salary">₹ {{ formatAmount(deduction.amount) }}</span>
            </div>
            <div class="detail-row total-row">
              <span class="label">TOTAL DEDUCTIONS</span>
              <span class="value_salary">₹ {{ formatAmount(totalDeductions) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Net Salary Section -->
      <div class="net-salary-section">
        <div class="net-salary-content">
          <h3>NET SALARY: ₹ {{ formatAmount(netSalary) }}</h3>
          <p>{{ numberToWords(netSalary) }} Rupees Only</p>
        </div>
      </div>
    </div>
    <!-- style="display: none" -->
    <!-- Signature Section - Dynamically added during PDF generation -->
    <div class="signature-section" id="signature-section">
      <div class="signature-container">
        <div class="signature-column">
          <div class="signature-label">Employer Signature</div>
          <v-img :src="employerSign" v-if="employerSign" class="employee-signature" contain></v-img>
          <div class="signature-line" v-else></div>
        </div>

        <div class="signature-column">
          <div class="signature-label">Employee Signature</div>
          <div class="signature-line"></div>
        </div>
      </div>
      <div class="signature-container">
        <div class="signature-column">
          <div class="signature-label">Date</div>
          <div class="signature-line"></div>
        </div>

        <div class="signature-column">
          <div class="signature-label">Date</div>
          <div class="signature-line"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fullName, getFullAddress } from '@/utils/common'

export default {
  name: 'SalarySlipPDFView',
  props: {
    employeeData: {
      type: Object,
      default: () => ({})
    },
    orgData: {
      type: Object,
      default: () => ({})
    },
    salaryData: {
      type: Object,
      default: null
    },
    selectedYear: {
      type: Number,
      default: new Date().getFullYear()
    },
    selectedMonth: {
      type: Number,
      default: new Date().getMonth()
    },
    standardDays: {
      type: Number,
      default: 0
    },
    workDays: {
      type: Number,
      default: 0
    },
    lopDays: {
      type: Number,
      default: 0
    },
    employerSignature: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      imageLoadError: false,
      orgLogoBase64: null
    }
  },
  computed: {
    employerSign() {
      const assetSign = require('@/assets/img/signature.png')
      return this.$props.employerSignature || assetSign
    },
    selectedMonthName() {
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ]
      return months[this.selectedMonth] || ''
    },
    formattedEarnings() {
      if (!this.salaryData || !this.salaryData.earnings) return []
      return this.salaryData.earnings.map((earning) => ({
        name: earning.name,
        label: earning.name,
        amount: earning.amount
      }))
    },
    formattedDeductions() {
      if (!this.salaryData || !this.salaryData.deductions) return []
      return this.salaryData.deductions.map((deduction) => ({
        name: deduction.name,
        label: deduction.name,
        amount: deduction.amount
      }))
    },
    totalEarnings() {
      if (!this.salaryData || !this.salaryData.earnings) return 0
      return this.salaryData.earnings.reduce((sum, earning) => sum + (earning.amount || 0), 0)
    },
    totalDeductions() {
      if (!this.salaryData || !this.salaryData.deductions) return 0
      return this.salaryData.deductions.reduce((sum, deduction) => sum + (deduction.amount || 0), 0)
    },
    netSalary() {
      return this.totalEarnings - this.totalDeductions
    },
    bankDetails() {
      return {
        bankName: this.employeeData?.employmentDetails?.bankName || '-',
        accountNumber: this.employeeData?.employmentDetails?.bankAccountNumber || '-'
      }
    },
    defaultLogo() {
      try {
        return require('@/assets/img/CW_DEFAULT.png')
      } catch (e) {
        return ''
      }
    },
    orgLogoUrl() {
      if (this.orgData && this.orgData.logo && !this.imageLoadError) {
        const logoPath = this.orgData.logo.startsWith('http')
          ? this.orgData.logo
          : `${process.env.VUE_APP_S3_BASE_URL || ''}${this.orgData.logo}`
        return logoPath
      }
      return null
    }
  },
  watch: {
    employeeData: {
      handler(newEmployeeData, oldEmployeeData) {
        if (newEmployeeData && newEmployeeData !== oldEmployeeData) {
          this.$forceUpdate()
        }
      },
      deep: true
    }
  },
  methods: {
    convertImageToBase64: async function (imageUrl) {
      try {
        console.log({ imageUrl })
        const response = await fetch(imageUrl, {
          method: 'GET',
          mode: 'cors'
        })
        console.log({ response }, 'kkkkk')
        const blob = await response.blob()

        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
      } catch (error) {
        console.error('Error converting image:', error)
        return null
      }
    },
    async convertLogoToBase64() {
      if (this.orgLogoUrl) {
        try {
          console.log(this.orgLogoUrl)
          const base64 = await this.convertImageToBase64(
            'https://uat-cdn.myfinfi.com/152/logo/53231758735158603.png'
          )
          console.log({ base64 })
          this.orgLogoBase64 = base64
        } catch (error) {
          console.error('Error converting logo to base64:', error)
          this.orgLogoBase64 = null
        }
      } else {
        this.orgLogoBase64 = null
      }
    },
    orgAddress(obj) {
      return getFullAddress(obj)
    },
    employeeFullName(item) {
      if (item) {
        return fullName(item)
      }
      return ''
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB')
    },
    formatAmount(amount) {
      // Convert to number if it's a string
      const num = typeof amount === 'string' ? parseFloat(amount) : amount
      // Return formatted amount with commas and 2 decimal places
      return isNaN(num)
        ? '0.00'
        : num.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
    },
    numberToWords(num) {
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
      const teens = [
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen'
      ]
      const tens = [
        '',
        '',
        'Twenty',
        'Thirty',
        'Forty',
        'Fifty',
        'Sixty',
        'Seventy',
        'Eighty',
        'Ninety'
      ]

      // Convert to integer to avoid decimal issues
      num = Math.round(parseFloat(num) || 0)

      if (num === 0) return 'Zero'

      function convertHundreds(n) {
        let result = ''
        if (n >= 100) {
          result += ones[Math.floor(n / 100)] + ' Hundred '
          n %= 100
        }
        if (n >= 20) {
          result += tens[Math.floor(n / 10)] + ' '
          n %= 10
        } else if (n >= 10) {
          result += teens[n - 10] + ' '
          return result.trim()
        }
        if (n > 0) {
          result += ones[n] + ' '
        }
        return result.trim()
      }

      let result = ''
      let crore = Math.floor(num / 10000000)
      if (crore > 0) {
        result += convertHundreds(crore) + ' Crore '
        num %= 10000000
      }

      let lakh = Math.floor(num / 100000)
      if (lakh > 0) {
        result += convertHundreds(lakh) + ' Lakh '
        num %= 100000
      }

      let thousand = Math.floor(num / 1000)
      if (thousand > 0) {
        result += convertHundreds(thousand) + ' Thousand '
        num %= 1000
      }

      if (num > 0) {
        result += convertHundreds(num)
      }

      return result.trim()
    },
    handleImageError() {
      this.imageLoadError = true
    }
  },
  mounted() {
    // Preload images to ensure they're available for PDF generation
    if (this.orgLogoUrl) {
      this.convertLogoToBase64()
    }
  }
}
</script>

<style scoped>
.salary-slip-pdf {
  /* font-family: Arial, sans-serif; */
  /* max-width: 800px; */
  margin: 0 auto;
  padding: 20px;
  background: white;
  color: black;
}

.pdf-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  margin-top: 10px;
}

.org-info-section {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.org-logo-container {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.org-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.org-details {
  text-align: left;
}

.org-name {
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 8px 0;
  color: #333;
}

.org-address {
  font-size: 12px;
  margin: 0;
  color: #666;
  line-height: 1.4;
}

.pay-period {
  text-align: right;
}

.pay-period h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #333;
}

.pay-period p {
  margin: 4px 0;
  font-size: 12px;
  color: #666;
}

.divider {
  height: 1px;
  background-color: #ccc;
  margin: 20px 0;
}

.divider-vertical {
  width: 1px;
  background-color: #ccc;
  margin: 0 50px;
}

.employee-info-section {
  margin-bottom: 20px;
}

.employee-details-grid {
  display: flex;
  justify-content: space-between;
}

.employee-details-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 12px;
}

.label {
  font-weight: bold;
  color: #333;
  width: 45%;
}

.value {
  color: #333;
  width: 55%;
  text-align: left;
}
.value_salary {
  color: #333;
  width: 55%;
  text-align: right;
}

.border-bottom {
  border-bottom: 1px dotted #ccc;
}

.total-row {
  background-color: #f5f5f5;
  font-weight: bold;
  padding: 6px 0;
  margin-top: 8px;
}

.salary-breakdown-section {
  margin-top: 20px;
}

.breakdown-container {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.earnings-section,
.deductions-section {
  flex: 1;
  border: 1px solid #ccc;
}

.section-header {
  background-color: #4a4a4a;
  color: white;
  padding: 8px;
  text-align: center;
}

.section-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: bold;
}

.section-content {
  padding: 12px;
}

.net-salary-section {
  border: 2px solid #4caf50;
  background-color: #f1f8e9;
  border-radius: 4px;
  padding: 15px;
  text-align: center;
}

.net-salary-content h3 {
  margin: 0 0 10px 0;
  font-size: 16px;
  color: #2e7d32;
  font-weight: bold;
}

.net-salary-content p {
  margin: 0;
  font-size: 12px;
  color: #666;
}

.signature-section {
  margin-top: 40px;
  opacity: 0;
}

.signature-container {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-top: 20px;
}

.signature-column {
  display: flex;
  width: 40%;
  text-align: center;
}

.signature-label {
  font-size: 12px;
  font-weight: bold;
  width: 200px;
  text-align: left;
  color: #333;
}

.signature-line {
  border-top: 1px solid #333;
  width: 100%;
  margin: auto;
}
.employee-signature {
  max-width: 120px;
  max-height: 40px;
  width: auto;
  height: auto;
  object-fit: contain;
}

@media print {
  .salary-slip-pdf {
    padding: 0;
    max-width: 100%;
  }
}
</style>
